import * as React from 'react'
import { Button, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap'

export type TabToggleFn = (activeTab: number) => void

export interface IWizardProps {
  hideNav?: boolean
  onTabChange?: (activeTab: number) => void
  onComplete?: () => void | Promise<any>
  noValidate?: boolean
  autoComplete?: 'on' | 'off'
  formRef?: (element: HTMLFormElement) => void
}

interface IWizardTabProps {
  header: string
  disabled?: boolean
  className?: string
  style?: React.CSSProperties
}

interface IWizardState {
  activeTab: number
  completing: boolean
}

const noop = () => { /**/ }

export class WizardTab extends React.Component<IWizardTabProps, {}> { }

// tslint:disable-next-line:max-classes-per-file
export class Wizard extends React.Component<IWizardProps, IWizardState> {
  public static Tab = WizardTab

  public state: IWizardState = {
    activeTab: 0,
    completing: false
  }

  public tabChange(activeTab: number) {
    const { onTabChange = noop, onComplete = noop } = this.props
    const count = React.Children.count(this.props.children)

    if (activeTab < 0) {
      activeTab = 0
    } else if (activeTab >= count) {
      activeTab = count - 1
      const completePromise = onComplete()

      if (completePromise) {
        this.setState({ completing: true })

        completePromise.finally(() => {
          this.setState({ completing: false })
        })
      }
    }

    this.setState({ activeTab }, () => {
      onTabChange(activeTab)
    })
  }

  public goBack() {
    const { activeTab } = this.state

    this.tabChange(activeTab - 1)
  }

  public submit(e: React.FormEvent<HTMLFormElement>) {
    const { activeTab } = this.state
    e.preventDefault()

    this.tabChange(activeTab + 1)
  }

  public render() {
    const { hideNav = false } = this.props
    const { activeTab, completing } = this.state
    const isFirst = activeTab === 0
    const isLast = activeTab === React.Children.count(this.props.children) - 1

    return (
      <form onSubmit={(e) => this.submit(e)} className="bl-wizard"
        noValidate={this.props.noValidate}
        autoComplete={this.props.autoComplete}
        ref={this.props.formRef}>
        <section className="bl-wizard-content">
          {!hideNav &&
            <Nav className="bl-wizard-nav">
              {React.Children.map(this.props.children, (child: any, index) => {
                const isActive = index === activeTab
                const isBeforeActive = index < activeTab
                let className = ''

                if (isActive) {
                  className = 'active'
                } else if (isBeforeActive) {
                  className = 'before-active'
                } else {
                  className = 'after-active'
                }

                return (
                  <NavItem>
                    <NavLink
                      className={className}
                      disabled={child.props.disabled}
                      onClick={() => { if (isBeforeActive) { this.tabChange(index) } }}>
                      {child.props.header}
                    </NavLink>
                  </NavItem>
                )
              })}
            </Nav>
          }

          <TabContent activeTab={activeTab}>
            {React.Children.map(this.props.children, (child: any, index) => {
              if (index !== activeTab) { return null }
              let { className = '' } = child.props
              className += ' bl-tab-pane'

              return (
                <TabPane key={child.props.header + index} tabId={index} className={className} style={child.props.style}>
                  {child.props.children}
                </TabPane>
              )
            })}
          </TabContent>
        </section>

        <footer className={`bl-wizard-footer btn-footer d-flex align-items-center ${isFirst ? 'justify-content-end' : 'justify-content-between'}`}>
          {!isFirst &&
            <Button className="bl-btn"
              color="light"
              size="lg" onClick={() => this.goBack()}
              disabled={completing}>
              Back
            </Button>
          }
          <Button className="bl-btn"
            color="secondary"
            size="lg"
            type="submit"
            disabled={completing}>
            {isLast ? (completing ? 'Finishing' : 'Finish') : 'Next Step'}
            {completing &&
              <>
                &nbsp;&nbsp;<i className="fa fa-fw fa-spin fa-sync" />
              </>
            }
          </Button>
        </footer>
      </form>
    )
  }
}
